import { FieldLabel, Skeleton } from '@/components/ui';
import { useAppData } from '@/hooks/useAppData';
import { useCountdown } from '@/hooks/useCountdown';
import { cn } from '@/lib/cn';
import { formatGameweek, formatRank } from '@/lib/format';

const PLACEHOLDER = '—';

/** Deadline turns urgent inside the last six hours. */
const URGENT_THRESHOLD_MS = 6 * 60 * 60 * 1000;

/** Only the deadline cell is accented; the rest inherit body colour. */
type Tone = 'deadline' | 'urgent' | 'locked' | undefined;

interface Cell {
  label: string;
  value: string;
  tone?: Tone;
  /**
   * True while the figure behind this cell is in flight. A request that failed
   * or came back empty is no longer loading, so the cell falls back to "—".
   */
  isLoading?: boolean;
}

/** The gameweek status strip that sits under the page title. */
export function GameweekBanner() {
  const { gameweek, summary, isGameweekLoading, isSummaryLoading } = useAppData();
  const countdown = useCountdown(gameweek?.deadlineAt ?? null);

  const isLocked = gameweek?.status === 'locked' || gameweek?.status === 'live';

  const deadlineValue = isLocked ? 'Squad locked' : countdown.label;
  const deadlineTone: Tone = isLocked
    ? 'locked'
    : countdown.remainingMs < URGENT_THRESHOLD_MS
      ? 'urgent'
      : 'deadline';

  const cells: Cell[] = [
    {
      label: gameweek ? `Gameweek ${formatGameweek(gameweek.id)}` : 'Gameweek',
      value: gameweek?.competition ?? PLACEHOLDER,
      isLoading: isGameweekLoading && !gameweek,
    },
    {
      label: isLocked ? 'Status' : 'Deadline',
      value: gameweek ? deadlineValue : PLACEHOLDER,
      // No tone on the placeholder, or a missing deadline would read as urgent.
      ...(gameweek ? { tone: deadlineTone } : {}),
      isLoading: isGameweekLoading && !gameweek,
    },
    {
      label: 'Current points',
      value: summary ? String(summary.gameweekPoints) : PLACEHOLDER,
      isLoading: isSummaryLoading && !summary,
    },
    {
      label: 'Overall rank',
      value: summary ? formatRank(summary.overallRank) : PLACEHOLDER,
      isLoading: isSummaryLoading && !summary,
    },
  ];

  return (
    <section className="mb-6 grid grid-cols-2 gap-px overflow-hidden rounded-panel border border-border bg-border sm:grid-cols-4">
      {cells.map((cell) => (
        <div key={cell.label} className="bg-canvas px-4 py-3.5">
          <FieldLabel>{cell.label}</FieldLabel>
          {cell.isLoading ? (
            // Sized to the line it replaces, so the strip does not resize.
            <Skeleton className="mt-1 h-[19px] w-24" />
          ) : (
            <strong
              className={cn(
                'mt-1 block text-[15px] font-extrabold tracking-[-0.3px]',
                cell.tone === 'deadline' && 'text-primary',
                cell.tone === 'urgent' && 'text-danger',
                cell.tone === 'locked' && 'text-warning',
              )}
            >
              {cell.value}
            </strong>
          )}
        </div>
      ))}
    </section>
  );
}
